import { useMemo } from 'react'
import { Paper, Typography, Box, Skeleton, useTheme } from '@mui/material'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { DailyDashboardStatDto } from '@api/types/dashboard'
import { CHART_COLORS } from './chartConfig'
import { useChartLayout } from './useChartLayout'

interface UserActivityChartProps {
  data?: DailyDashboardStatDto[]
  isLoading?: boolean
}

export function UserActivityChart({ data, isLoading }: UserActivityChartProps) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const layout = useChartLayout()

  const chartData = useMemo(() => {
    if (!data) return []
    return data.map((item) => ({
      date: new Date(item.date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
      activeUsers: item.activeUsers,
      newUsers: item.newUsers,
    }))
  }, [data])

  const gridColor = isDark ? CHART_COLORS.gridDark : CHART_COLORS.grid
  const axisColor = isDark ? CHART_COLORS.axisDark : CHART_COLORS.axis

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 }, height: '100%' }}>
      <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
        User Activity
      </Typography>
      {isLoading ? (
        <Box sx={{ height: layout.height }}>
          <Skeleton variant="rectangular" height="100%" />
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={layout.height}>
          <LineChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="date" tick={{ fill: axisColor, fontSize: layout.fontSize }} minTickGap={layout.minTickGap} />
            <YAxis tick={{ fill: axisColor, fontSize: layout.fontSize }} width={layout.yAxisWidth} allowDecimals={false} />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
            />
            <Legend wrapperStyle={{ fontSize: layout.fontSize + 2 }} />
            <Line
              type="monotone"
              dataKey="activeUsers"
              name="Active Users"
              stroke={CHART_COLORS.activeUsers}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="newUsers"
              name="New Users"
              stroke={CHART_COLORS.newUsers}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Paper>
  )
}
